import type { ReactNode } from 'react';

import { cn } from '../lib/cn.js';

/**
 * Empty, loading and error states.
 *
 * Centralised because these are the screens users hit when something is wrong,
 * and they are exactly what gets improvised differently on every page.
 */
export const EmptyState = ({
  title,
  description,
  action,
  className,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}): React.JSX.Element => (
  <div className={cn('flex flex-col items-center gap-2 px-6 py-14 text-center', className)}>
    <p className="text-sm font-medium">{title}</p>
    {description && <p className="max-w-sm text-sm text-muted-foreground">{description}</p>}
    {action && <div className="mt-2">{action}</div>}
  </div>
);

/** Shimmer placeholder sized like the content it replaces. */
export const Skeleton = ({ className }: { className?: string }): React.JSX.Element => (
  <div className={cn('animate-pulse rounded bg-muted', className)} />
);

export const SkeletonRows = ({
  rows = 4,
  cols = 4,
}: {
  rows?: number;
  cols?: number;
}): React.JSX.Element => (
  <>
    {Array.from({ length: rows }, (_, r) => (
      <tr key={r} className="border-b last:border-0">
        {Array.from({ length: cols }, (_, c) => (
          <td key={c} className="px-4 py-3">
            {/* Varying widths so it reads as content, not a progress bar. */}
            <Skeleton className={c === 0 ? 'h-4 w-40' : 'h-4 w-16'} />
          </td>
        ))}
      </tr>
    ))}
  </>
);

export const ErrorState = ({
  message,
  className,
}: {
  message: string;
  className?: string;
}): React.JSX.Element => (
  <div className={cn('px-6 py-12 text-center', className)}>
    <p className="text-sm font-medium text-destructive">Something went wrong</p>
    <p className="mt-1 text-sm text-muted-foreground">{message}</p>
  </div>
);
